<template>
  <div>
    <Breadcrumbs
      :paths="['/file', $route.path]"
    ></Breadcrumbs>
    <v-toolbar>
      <v-toolbar-title>
        {{ $t("message.file.file_detail") }}&nbsp;({{ $route.params.fid }})
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-icon v-on="on" class="ma-2" @click="get_detail_data(get_prefix() + 'file/', [$route.params.fid])">
            mdi-refresh
          </v-icon>
        </template>
        <span>{{ $t("message.common.refresh") }}</span>
      </v-tooltip>
    </v-toolbar>
    <v-card class="pa-5 mt-1" v-if="fetch_status === 'getting'">
      {{ $t("message.common.getting", { "id": $route.params.fid }) }}
      <p class="text-right">
        <v-btn :to="get_prev(get_prefix() + 'file')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <v-card class="pa-5 mt-1" v-else-if="data">
      <v-simple-table>
        <template v-slot:default>
          <tbody>
            <tr>
              <td>
                {{ $i18n.t("message.common.fid") }}
              </td>
              <td>
                {{ data.fid }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.file.file") }}
              </td>
              <td class="table-name">
                <template v-if="changeable()">
                  <v-card
                    class="d-flex justify-space-between"
                    flat
                  >
                    <v-card flat class="align-self-center flex-grow-1">
                      <v-text-field
                        v-model="data.name"
                        :rules="[
                          v => (v && v.length <= 256) || $t('message.common.over_text', { length: 256 })
                        ]"
                        counter="256"
                      >
                      </v-text-field>
                    </v-card>
                    <template v-if="data.name !== original_data.name">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" class="ma-2" @click="data.name = original_data.name">
                            mdi-undo-variant
                          </v-icon>
                        </template>
                        <span>{{ $t("message.common.undo") }}</span>
                      </v-tooltip>
                    </template>
                  </v-card>
                </template>
                <template v-else>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        {{ data.name }}
                      </span>
                    </template>
                    <span>{{ data.name }}</span>
                  </v-tooltip>
                </template>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.type") }}
              </td>
              <td>
                {{ $i18n.t("message.file.file_type_" + data.type) }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.size") }}(KB)
              </td>
              <td>
                {{ (data.size / 1024).toFixed(1) | comma_digit }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.common.group") }}
              </td>
              <td>
                <IdName
                  :mode="mode"
                  type="group"
                  :ids="[data.gid]"
                  :nav_path="nav_path"
                ></IdName>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.common.user") }}
              </td>
              <td>
                <IdName
                  :mode="mode"
                  type="user"
                  :ids="[data.uid]"
                  :nav_path="nav_path"
                ></IdName>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.permission") }}
              </td>
              <td class="pt-0 pb-0">
                <template v-if="changeable()">
                  <v-card
                    class="d-flex justify-space-between"
                    flat
                  >
                    <v-card flat class="align-self-center flex-grow-1">
                      <v-select
                        :items="permissions"
                        item-text="label"
                        item-value="value"
                        v-model="data.permission"
                        single-line
                        hide-details
                        dense
                      >
                        <template v-slot:item="props">
                          {{ $t('message.common.' + props.item.label) }}
                        </template>
                        <template v-slot:selection="props">
                          {{ $t('message.common.' + props.item.label) }}
                        </template>
                      </v-select>
                    </v-card>
                    <template v-if="data.permission !== original_data.permission">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" class="ma-2" @click="data.permission = original_data.permission">
                            mdi-undo-variant
                          </v-icon>
                        </template>
                        <span>{{ $t("message.common.undo") }}</span>
                      </v-tooltip>
                    </template>
                  </v-card>
                </template>
                <template v-else>
                  {{ $t('message.common.' + data.permission) }}
                </template>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.ctime") }}
              </td>
              <td>
                {{ $d(1000 * data.ctime, 'long', $store.getters.langs[$i18n.locale].locale) }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.etime") }}
              </td>
              <td>
                {{ $d(1000 * data.etime, 'long', $store.getters.langs[$i18n.locale].locale) }}
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.remark") }}
              </td>
              <td>
                <FileRemark
                  :fid="data.fid"
                  :remarks="data.remarks"
                  :mode="mode"
                  :nav_path="nav_path"
                  :visible="['estimate', 'similarity']"
                >
                </FileRemark>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.file.download") }}
              </td>
              <td class="pt-0 pb-0">
                <template v-if="data.type !== 'similarity' && downloadable">
                  <v-list v-for="extension in downloadable" :key="extension">
                    <v-list-item dense @click="get_download(data.fid, extension)">
                      <v-list-item-icon>
                        <v-icon>mdi-file</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>
                        {{ $t('message.file.download_as', { "extension": $t('message.common.extension_' + extension) } ) }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </template>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.viewer.preview") }}
              </td>
              <td class="pt-0 pb-0">
                <template v-if="data.type !== 'similarity' && previewable">
                  <v-list v-for="extension in previewable" :key="extension">
                    <v-list-item dense @click="$refs.viewer && $refs.viewer.open(data.fid, extension)">
                      <v-list-item-icon>
                        <v-icon>mdi-file-find</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>
                        {{ $t('message.viewer.preview_as', { "extension": $t('message.common.extension_' + extension) } ) }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </template>
              </td>
            </tr>
            <tr>
              <td>
                {{ $i18n.t("message.common.operation") }}
              </td>
              <td>
                <div class="buttonContents">
                  <template v-if="mode === 'user' && data.type === 'source'">
                    <v-btn
                      color="primary"
                      @click="$router.push('/translate?source=' + data.fid)"
                    >
                      {{ $t("message.file.translate_this") }}
                    </v-btn>
                    <!--
                    <template v-if="
                      data.extensions.indexOf('json') >= 0
                      && deletable()
                      && ($store.getters.user && ['the-reading'].indexOf($store.getters.user.domain) < 0)
                    ">
                      <v-btn
                        color="primary"
                        @click="$router.push('/editor/source/' + data.fid + '?referer=' + nav_path)"
                      >
                        {{ $t("message.file.edit_source") }}
                      </v-btn>
                    </template>
                    -->
                  </template>
                  <template v-else-if="
                    mode === 'user'
                    && data.type === 'translated'
                    && data.extensions.indexOf('json') >= 0
                    && ($store.getters.user && ['the-reading'].indexOf($store.getters.user.domain) < 0)
                  ">
                    <template v-if="deletable()">
                      <v-btn
                        color="primary"
                        @click="$router.push('/editor/translated/' + data.fid + '?referer=' + nav_path)"
                      >
                        {{ $t("message.file.edit_translated") }}
                      </v-btn>
                    </template>
                    <v-btn
                      color="primary"
                      @click="import_as_corpus(data.fid, data.name)"
                    >
                      {{ $t("message.file.import_as_corpus") }}
                    </v-btn>
                  </template>
                  <template v-if="deletable()">
                    <v-btn
                      @click="confirm_delete_data(get_prefix() + 'file', [data.fid])"
                    >
                      {{ $t("message.common.delete") }}
                    </v-btn>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="text-right">
        <template v-if="changeable()">
          <v-btn
            color="primary"
            :disabled="!modifiable"
            @click="change_data('file', [data.fid])"
          >
            {{ $t("message.common.modify") }}
          </v-btn>
        </template>
        <v-btn :to="get_prev(get_prefix() + 'file')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <v-card class="pa-5 mt-1" v-else>
      {{ $t("message.common.no_id", { "id": $route.params.fid }) }}
      <p class="text-right">
        <v-btn :to="get_prev(get_prefix() + 'file')">
          {{ $t("message.common.prev") }}
        </v-btn>
      </p>
    </v-card>
    <Confirm
      ref="delete_confirm"
      :message="$t('message.common.delete_confirm')"
      :positive="$t('message.common.yes')"
      :negative="$t('message.common.no')"
    >
    </Confirm>
    <Confirm
      ref="import_as_corpus_confirm"
      negative="OK"
    >
      <Complete
        command_type="corpus_import"
        :jobs="import_as_corpus_jobs"
        :show_link="false"
        :mode="mode"
      >
        <template v-slot:title>
          <v-card flat class="pb-2">
            {{ $t("message.file.accept_import_as_corpus") }}
          </v-card>
        </template>
      </Complete>
    </Confirm>
    <Viewer
      ref="viewer"
      :mode="mode"
    >
    </Viewer>
  </div>
</template>

<script>
import Breadcrumbs from '../components/Breadcrumbs';
import IdName from '../components/IdName';
import FileRemark from '../components/FileRemark';
import Confirm from '../components/Confirm';
import Viewer from '../components/Viewer';
import Complete from '../components/Complete';
import { res_detail } from "../js/res_detail";
import { get_download } from "../js/get_download";
import { http_error } from "../js/http_error";

export default {
  name: 'file_detail',
  components: {
    Breadcrumbs,
    IdName,
    FileRemark,
    Confirm,
    Viewer,
    Complete
  },
  mixins: [
    res_detail,
    get_download,
    http_error
  ],
  props: {
    mode: String
  },
  data: function(){
    return {
      module_id: "23",
      nav_path: null,
      fetch_status: "getting",
      data: null,
      original_data: null,
      editable: {
        permission: () => true,
        name: () => (this.data && this.data.name.length > 0 && this.data.name.length <= 256)
      },
      file_types: [
        {
          "label": "all",
          "value": "all"
        },
        {
          "label": "source",
          "value": "source"
        },
        {
          "label": "translated",
          "value": "translated"
        },
        {
          "label": "corpus",
          "value": "corpus"
        }
      ],
      permissions: [
        {
          "label": "user",
          "value": "user"
        },
        {
          "label": "group",
          "value": "group"
        },
        {
          "label": "organization",
          "value": "organization"
        }
      ],
      import_as_corpus_jobs: []
    }
  },
  mounted: function(){
    this.get_detail_data(this.get_prefix() + 'file/', [this.$route.params.fid]);
  },
  methods: {
    import_as_corpus: function(fid, name=null){
      var parameters = {
        corpus_type: "translated",
        corpus: [fid],
        type: "corpus"
      };
      if(name === null){
        parameters.title = fid;
      }else{
        parameters.title = name.replace(/\([A-Z]\d{5,8}\)/g, "") + "(" + fid + ")";
      }
      this.$http.post(this.$store.getters.api_endpoint + "/" + this.get_prefix() + "action/corpus_import",
                      parameters,
                      {
                        headers: {
                          'Content-Type': 'application/json'
                        },
                      })
        .then((response) => {
          if(!this.$refs.import_as_corpus_confirm){
            return;
          }
          this.import_as_corpus_jobs = [{
            jid: response.data.jid,
            name: response.data.title,
            status: "complete"
          }];
          this.$refs.import_as_corpus_confirm.open(null, 600)
            .then((result) => {
              this.import_as_corpus_jobs = [];
            });
        })
        .catch((error) => {
          if(this.http_error(error)){
            this.$store.commit("setNotification", {
              type: "error",
              message: error.response.data
            });
          }
        });
    }
  },
  computed: {
    downloadable: function(){
      if(!this.data){
        return null;
      }
      var new_extensions = [];
      for(var extension of this.data.extensions){
        if(["cnt", "sim", "json"].indexOf(extension) === -1){
          new_extensions.push(extension);
        }
      }
      if(new_extensions.length === 0){
        return null;
      }
      return new_extensions;
    },
    previewable: function(){
      if(!this.data){
        return null;
      }
      var new_extensions = [];
      for(var extension of this.data.extensions){
        if(['txt', 'docx', 'xlsx', 'pptx', 'pdf', 'ocr', 'json'].indexOf(extension) >= 0){
          new_extensions.push(extension);
        }
      }
      return new_extensions;
    }
  }
}
</script>

<style scoped>
.v-data-table
  tbody
  tr:hover:not(.v-data-table__expanded__content) {
  background: inherit !important;
}
.v-data-table td:first-of-type {
  white-space : nowrap;
}
.buttonContents {
  margin-bottom: 8px;
}
.buttonContents > button {
  margin-top: 8px;
  margin-right: 8px;
}
.v-data-table {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 12px;
}
.v-data-table
  tbody > tr > td:first-of-type {
  background-color: #eeeeee;
}
.table-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
}
</style>
